import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import { or } from 'tio-ui/utilities';
import {
  PhTrophy,
  PhStar,
  PhRocket,
  PhTarget,
  PhCrown,
  PhMedal,
  PhCheckCircle,
  PhShare,
  PhDownload,
  PhFlag,
} from 'ember-phosphor-icons';
import type { PslfAchievement } from 'tio-employee/services/career-pathways-api';

interface MilestoneBadgeSignature {
  Args: {
    achievement: PslfAchievement;
    onShare?: (achievement: PslfAchievement) => void;
    onDownload?: (achievement: PslfAchievement) => void;
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

// Map milestone types to icons. The per-tier gradient is a CSS recipe
// (`.tio-milestone-tier--<type>`) keyed off the milestone type, so the
// achievement-tier color encoding lives in the stylesheet, not inline.
function getMilestoneIcon(type: PslfAchievement['milestoneType']) {
  const icons: Record<string, typeof PhTrophy> = {
    started: PhFlag,
    first_year: PhStar,
    two_years: PhTarget,
    four_years: PhMedal,
    halfway: PhRocket,
    six_years: PhTrophy,
    eight_years: PhTrophy,
    home_stretch: PhRocket,
    final_countdown: PhTarget,
    forgiveness: PhCrown,
  };
  return icons[type] || icons.started;
}

export default class PslfMilestoneBadge extends Component<MilestoneBadgeSignature> {
  @tracked showConfetti = false;

  get icon() {
    return getMilestoneIcon(this.args.achievement.milestoneType);
  }

  get tierClass() {
    return `tio-milestone-tier--${this.args.achievement.milestoneType}`;
  }

  get isUnlocked() {
    return this.args.achievement.achievedAt !== null;
  }

  get formattedDate() {
    if (!this.args.achievement.achievedAt) return null;
    return new Date(this.args.achievement.achievedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get isForgiveness() {
    return this.args.achievement.milestoneType === 'forgiveness';
  }

  handleShare = () => {
    this.args.onShare?.(this.args.achievement);
  };

  handleDownload = () => {
    this.args.onDownload?.(this.args.achievement);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if @compact}}
      {{! Compact badge view for grids }}
      <div
        class="tio-pslf-milestone tio-pslf-milestone--compact"
        title="{{@achievement.badgeName}}"
        ...attributes
      >
        <div
          class="tio-pslf-milestone__compact-disc
            {{if
              this.isUnlocked
              (concat 'tio-milestone-tier ' this.tierClass)
              'tio-pslf-milestone__compact-disc--locked'
            }}"
        >
          {{#if this.isUnlocked}}
            <span class="tio-pslf-milestone__compact-emoji">{{@achievement.badgeEmoji}}</span>
          {{else}}
            <this.icon @size={{28}} class="tio-milestone-locked-glyph" />
          {{/if}}
        </div>
        {{#unless this.isUnlocked}}
          <div class="tio-pslf-milestone__compact-lock">
            <div class="tio-pslf-milestone__compact-outline"></div>
          </div>
        {{/unless}}
        {{#if this.isForgiveness}}
          <div class="tio-pslf-milestone__crown">
            <span>👑</span>
          </div>
        {{/if}}
      </div>
    {{else}}
      {{! Full achievement card }}
      <div class="tio-milestone-card tio-pslf-milestone" ...attributes>
        {{! Header with badge }}
        <div
          class="tio-pslf-milestone__header
            {{if
              this.isUnlocked
              (concat 'tio-milestone-tier ' this.tierClass)
              'tio-pslf-milestone__header--locked'
            }}"
        >
          {{! Decorative elements }}
          {{#if this.isUnlocked}}
            <div class="absolute inset-0 overflow-hidden">
              <div class="tio-pslf-milestone__orb tio-pslf-milestone__orb--top"></div>
              <div class="tio-pslf-milestone__orb tio-pslf-milestone__orb--bottom"></div>
            </div>
          {{/if}}

          {{! Badge icon }}
          <div
            class="tio-pslf-milestone__badge
              {{if
                this.isUnlocked
                'tio-pslf-milestone__badge--unlocked'
                'tio-pslf-milestone__badge--locked'
              }}"
          >
            {{#if this.isUnlocked}}
              <span class="text-4xl">{{@achievement.badgeEmoji}}</span>
            {{else}}
              <this.icon @size={{40}} class="tio-milestone-locked-glyph" />
            {{/if}}
          </div>

          {{! Payment count badge }}
          {{#if this.isUnlocked}}
            <div class="tio-pslf-milestone__payment-count">
              {{@achievement.payments}}
              payments
            </div>
          {{/if}}

          {{! Locked overlay }}
          {{#unless this.isUnlocked}}
            <div class="tio-pslf-milestone__locked-overlay">
              <span class="text-4xl opacity-60">🔒</span>
            </div>
          {{/unless}}
        </div>

        {{! Content }}
        <div class="tio-pslf-milestone__content">
          <div class="text-center mb-4">
            <h3
              class="tio-pslf-milestone__title
                {{unless this.isUnlocked 'tio-pslf-milestone__title--locked'}}"
            >
              {{@achievement.badgeName}}
            </h3>
            <Text
              @size="sm"
              class="tio-pslf-milestone__message tio-fg-disabled
                {{unless this.isUnlocked 'tio-pslf-milestone__message--locked'}}"
            >
              {{@achievement.message}}
            </Text>
          </div>

          {{#if this.isUnlocked}}
            {{! Unlocked state }}
            <div class="tio-milestone-unlocked tio-pslf-milestone__unlocked">
              <PhCheckCircle @size={{16}} />
              <span>Unlocked {{this.formattedDate}}</span>
            </div>

            {{! Stats if available }}
            {{#if @achievement.statsAtMilestone}}
              <div class="tio-pslf-milestone__stats">
                <div class="tio-pslf-milestone__stats-grid">
                  <div>
                    <Text @size="lg" @weight="bold" class="tio-fg-inverse">
                      {{@achievement.statsAtMilestone.percentComplete}}%
                    </Text>
                    <Text @size="xs" class="tio-fg-muted">Complete</Text>
                  </div>
                  <div>
                    <Text @size="lg" @weight="bold" class="tio-fg-inverse">
                      ${{@achievement.statsAtMilestone.forgivenessEarned}}
                    </Text>
                    <Text @size="xs" class="tio-fg-muted">Earned</Text>
                  </div>
                </div>
              </div>
            {{/if}}

            {{! Share/Download actions }}
            {{#if (or @onShare @onDownload)}}
              <div class="tio-flex-tight">
                {{#if @onShare}}
                  <Button
                    @intent="primary"
                    @appearance="minimal"
                    class="flex-1 justify-center"
                    {{on "click" this.handleShare}}
                  >
                    <PhShare @size={{16}} />
                    <span>Share</span>
                  </Button>
                {{/if}}
                {{#if @onDownload}}
                  <Button
                    @intent="primary"
                    @appearance="minimal"
                    class="flex-1 justify-center"
                    {{on "click" this.handleDownload}}
                  >
                    <PhDownload @size={{16}} />
                    <span>Certificate</span>
                  </Button>
                {{/if}}
              </div>
            {{/if}}
          {{else}}
            {{! Locked state - show target payments }}
            <div class="tio-pslf-milestone__locked-copy">
              <Text @size="sm" class="tio-fg-muted">
                Reach
                {{@achievement.payments}}
                payments to unlock
              </Text>
            </div>
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
